import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import {
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
	sortByPropertyAlphabetically,
	isNotNullOrEmpty,
} from "utils";
import TrainingListing from "./training-listing";
import { EditStaffTrainingForm } from "features/health-safety-forms";

const useStyles = makeStyles((theme) => ({
	root: {
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.white,
		overflow: "hidden",
		boxShadow: "none",
		"&:before": {
			display: "none",
		},
	},
	summary: {
		padding: theme.spacing(0, 1.5, 0, 2),
		minHeight: 52,
		"&.Mui-expanded": {
			minHeight: 52,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(1.25, 0),
			alignItems: "center",
		},
	},
	summaryTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary,
	},
	countMuted: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginLeft: theme.spacing(0.5),
	},
	expandIcon: {
		color: theme.palette.text.secondary,
	},
	details: {
		padding: theme.spacing(0, 1.5, 2, 2),
		display: "block",
	},
	list: {
		marginTop: theme.spacing(1),
	},
}));

const staffHasTraining = (training, staffId) =>
	training?.people && JSON.stringify(training.people).includes(staffId);

const StaffTrainingPanel = ({ staffId, expanded, onExpandedChange }) => {
	const classes = useStyles();
	const [editId, setEditId] = useState(null);
	const staffTrainingEnabled = useSelector(
		({ featureFlags }) => featureFlags?.staffTraining === true
	);

	const staffTrainingList = useSelector(
		({ healthSafety }) => healthSafety.staffTrainings?.results
	);

	const trainings = useMemo(() => {
		if (!staffId || !staffTrainingList) {
			return [];
		}
		return staffTrainingList
			.filter((node) => staffHasTraining(node, staffId))
			.sort(sortByPropertyAlphabetically("name"));
	}, [staffId, staffTrainingList]);

	if (!staffTrainingEnabled || !staffId || trainings.length === 0) {
		return null;
	}

	const countLabel = ` (${trainings.length})`;

	return (
		<>
			<ExpansionPanel
				className={classes.root}
				elevation={0}
				expanded={expanded}
				onChange={onExpandedChange}
				data-cy="staff-training-panel"
			>
				<ExpansionPanelSummary
					expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}
					className={classes.summary}
					aria-controls="staff-training-panel-content"
					id="staff-training-panel-header"
				>
					<Typography component="h2" className={classes.summaryTitle}>
						Training
						<span className={classes.countMuted}>{countLabel}</span>
					</Typography>
				</ExpansionPanelSummary>
				<ExpansionPanelDetails className={classes.details}>
					<div className={classes.list}>
						{isNotNullOrEmpty(trainings) &&
							trainings.map((node, index) => (
								<TrainingListing
									key={node.id || index}
									item={node}
									onEdit={(id) => setEditId(id)}
								/>
							))}
					</div>
				</ExpansionPanelDetails>
			</ExpansionPanel>
			{editId && (
				<EditStaffTrainingForm
					readOnly
					id={editId}
					onClose={() => setEditId(null)}
				/>
			)}
		</>
	);
};

StaffTrainingPanel.propTypes = {
	staffId: PropTypes.string,
	expanded: PropTypes.bool.isRequired,
	onExpandedChange: PropTypes.func.isRequired,
};

export default StaffTrainingPanel;
